import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Card, CardContent, IconButton, Paper, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { Building } from '../../../types';
import { EditableBuildingName } from './editable-building-name';

interface BuildingCarouselProps {
  buildings: Building[];
  selectedBuildingId: number | null;
  onBuildingSelect: (buildingId: number) => void;
  onDelete: (buildingId: number) => void;
  onClone: (buildingId: number) => void;
  onUpdateName?: (buildingId: number, newName: string) => Promise<void>;
  isUpdatingName?: boolean;
  isCloningBuilding?: boolean;
  readOnly?: boolean;
}

export const BuildingCarousel: React.FC<BuildingCarouselProps> = ({
  buildings,
  selectedBuildingId,
  onBuildingSelect,
  onDelete,
  onClone,
  onUpdateName,
  isUpdatingName = false,
  isCloningBuilding = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const selectedCardRef = useRef<HTMLDivElement>(null);

  // Scroll the selected building card into view when selection changes
  useEffect(() => {
    if (selectedCardRef.current && scrollContainerRef.current) {
      selectedCardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [selectedBuildingId]);

  // Memoize existing names map for efficient lookup
  const existingNamesMap = useMemo(() => {
    const map = new Map<number, string[]>();
    buildings.forEach(building => {
      const otherBuildings = buildings.filter(b => b.id !== building.id);
      map.set(
        building.id,
        otherBuildings.map(b => b.name)
      );
    });
    return map;
  }, [buildings]);

  const scrollBuildings = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {buildings.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          {/* Left Scroll Button */}
          <IconButton
            onClick={() => scrollBuildings('left')}
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {/* Right Scroll Button */}
          <IconButton
            onClick={() => scrollBuildings('right')}
            sx={{
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          {/* Scrollable Building Cards */}
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'divider',
                borderRadius: 4,
              },
              pb: 1,
            }}
          >
            {buildings.map(building => (
              <Card
                key={building.id}
                ref={selectedBuildingId === building.id ? selectedCardRef : undefined}
                sx={{
                  minWidth: 200,
                  maxWidth: 200,
                  cursor: 'pointer',
                  border:
                    selectedBuildingId === building.id
                      ? `2px solid ${theme.palette.primary.main}`
                      : '1px solid',
                  borderColor:
                    selectedBuildingId === building.id ? theme.palette.primary.main : 'divider',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
                onClick={() => onBuildingSelect(building.id)}
              >
                <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
                  {/* Building Name Label - Editable */}
                  {onUpdateName ? (
                    <EditableBuildingName
                      name={building.name}
                      buildingId={building.id}
                      existingNames={existingNamesMap.get(building.id) || []}
                      onSave={onUpdateName}
                      isUpdating={isUpdatingName}
                      variant="carousel"
                    />
                  ) : (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.primary',
                        fontSize: '1rem',
                      }}
                    >
                      {building.name}
                    </Typography>
                  )}
                  {/* Placeholder icon */}
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 1,
                      border: '2px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="h6">X</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                    ${(building.totalPrice || 0).toFixed(2)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={readOnly}
                      onClick={e => {
                        e.stopPropagation();
                        onDelete(building.id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={isCloningBuilding || readOnly}
                      onClick={e => {
                        e.stopPropagation();
                        onClone(building.id);
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};
